import { Anfrage } from "@/model/Anfrage";
import { Partner } from "@/model/Partner";
import { Vorschaden } from "@/model/Vorschaden";
import { Vorversicherung } from "@/model/Vorversicherung";
import Logger from "js-logger";
import dateUtils from "@/businesslogic/utils/dateUtils";
import storeUtils from "@/businesslogic/utils/storeUtils";

const { getIsoDateNow, pruefeNach12Uhr, pruefe2902, getIsoDateNowPlus } = dateUtils();
const { enthaeltVGV, istFhkVerkaufsprodukt } = storeUtils();

export default function anfrageBereinigung(anfrage: Anfrage): void {
  bereinigeKundendaten(anfrage);
  bereinigeVerwalterdaten(anfrage);
  bereinigeGebaeudedaten(anfrage);
  if (istFhkVerkaufsprodukt(anfrage)) {
    bereinigeFerienhaus(anfrage);
  }
  bereinigeVericherungsbeginn(anfrage);

  function bereinigeKundendaten(anfrage: Anfrage) {
    if (!anfrage.tarifinformationen.hugMitglied) {
      anfrage.personendaten.versicherungsnehmer.hugMitgliedNummer = "";
      anfrage.personendaten.versicherungsnehmer.hugMitgliedOrt = "";
    }
    if (!["0003", "etg", "weg"].includes(anfrage.personendaten.versicherungsnehmer?.anrede.toLowerCase())) {
      anfrage.personendaten.versicherungsnehmer.organisation = "";
    }
    if (!anfrage.personendaten.verwalterAktiv) {
      anfrage.personendaten.verwalter = new Partner();
    }
    if (anfrage.zahlungsdaten.zahlungsart.toLowerCase() == "debit") {
      anfrage.zahlungsdaten.iban = anfrage.zahlungsdaten.iban.replace(/ /g, "");
      if (anfrage.zahlungsdaten.kontoinhaberPartnerRolle != "anderePerson") {
        anfrage.zahlungsdaten.kontoinhaberPartner = new Partner();
      }
    } else {
      anfrage.zahlungsdaten.iban = "";
      anfrage.zahlungsdaten.kontoinhaberPartnerRolle = "";
      anfrage.zahlungsdaten.kontoinhaberPartner = new Partner();
    }
    if (!istFhkVerkaufsprodukt(anfrage)) {
      bereinigeVorschaeden(
        anfrage.tarifinformationen.vorschaedenAnzahl,
        anfrage.vorschaeden,
        anfrage.verkaufsprodukt.toLowerCase() === "phv" && !anfrage.tarifinformationen.hundehalterhaftpflicht,
      );
    }
    if (!emailValid(anfrage.personendaten.versicherungsnehmer.email)) {
      anfrage.personendaten.versicherungsnehmer.email = "";
    }
    bereinigeVorversicherung(anfrage.vorversicherung, anfrage.verkaufsprodukt);
  }

  function bereinigeVerwalterdaten(anfrage: Anfrage) {
    if ("0003" !== anfrage.personendaten.verwalter?.anrede) {
      anfrage.personendaten.verwalter.organisation = "";
    }
  }

  function bereinigeVorschaeden(anzahl: number, vorschaeden: Vorschaden[], entferneSchadenart = false): void {
    if (vorschaeden !== null && anzahl < vorschaeden.length) {
      vorschaeden.splice(anzahl);
    }
    for (let i = vorschaeden.length - 1; i >= 0; i--)
      if (vorschaeden[i].schadenjahr === null || !vorschaeden[i].schadenjahr) {
        Logger.debug("anfrageBereinigung: Vorschaden-Element war leer und wurde bereinigt.");
        vorschaeden.splice(i, 1);
      }
    if (entferneSchadenart) {
      // Nachträglich ausgeblendete Schadenart bei Abwahl Hunde-HV-Modul in PHV entfernen
      for (const vorschaden of vorschaeden) {
        vorschaden.schadenart = "";
      }
    }
  }

  function bereinigeVorversicherung(vorversicherung: Vorversicherung, verkaufsprodukt: string): void {
    // Beachte auch bereinigeVorversicherung() in vorversicherungsUtils.ts
    const art = vorversicherung?.antragsart?.toLowerCase();
    const produkt = verkaufsprodukt?.toLowerCase();
    if ((art === "neu" && produkt !== "vgv") || (art === "neu" && produkt === "vgv" && !vorversicherung.vorhanden)) {
      vorversicherung.gesellschaft = "";
      vorversicherung.versicherungsscheinnummer = "";
      vorversicherung.vertragEnde = "";
      vorversicherung.gekuendigtVon = "";
    } else if (vorversicherung.antragsart?.toLowerCase() == "aenderung") {
      vorversicherung.vertragEnde = "";
    }
  }

  function bereinigeGebaeudedaten(anfrage: Anfrage) {
    if (
      (anfrage.verkaufsprodukt.toLowerCase() === "vgv" && anfrage.gebaeudedaten.gebaeudenutzung != "0003") ||
      (anfrage.verkaufsprodukt.toLowerCase() === "hg" && anfrage.gebaeudedaten.gebaeudenutzung != "0005")
    ) {
      anfrage.gebaeudedaten.leerstand = 0;
    }
    if (!anfrage.gebaeudedaten.gewerbeVorhanden) {
      anfrage.gebaeudedaten.gewerbeAnzahl = 0;
      anfrage.gebaeudedaten.gewerbeListe.length = 0;
    } else if (
      anfrage.gebaeudedaten.gewerbeAnzahl &&
      anfrage.gebaeudedaten.gewerbeAnzahl < anfrage.gebaeudedaten.gewerbeListe.length
    ) {
      anfrage.gebaeudedaten.gewerbeListe.splice(anfrage.gebaeudedaten.gewerbeAnzahl);
    }

    if (!anfrage.gebaeudedaten.parkplaetzeVorhanden) {
      anfrage.gebaeudedaten.anzahlGaragen = 0;
      anfrage.gebaeudedaten.anzahlCarports = 0;
    }
    if (!anfrage.gebaeudedaten.kellerVorhanden) {
      anfrage.gebaeudedaten.wohnflaecheKeller = 0;
    }
    if (!anfrage.gebaeudedaten.photovoltaikVorhanden) {
      anfrage.gebaeudedaten.photovoltaikBaujahr = 0;
      anfrage.gebaeudedaten.photovoltaikLeistung = 0;
    }
    if (!anfrage.tarifinformationen.kernsanierung) {
      anfrage.gebaeudedaten.sanierungsdaten.kernsanierungsjahr = null;
    }
    if (!anfrage.gebaeudedaten.sanierungsdaten.dachbedeckung) {
      anfrage.gebaeudedaten.sanierungsdaten.dachbedeckungJahr = null;
    }
    if (!anfrage.gebaeudedaten.sanierungsdaten.leitungenElektrik) {
      anfrage.gebaeudedaten.sanierungsdaten.leitungenElektrikJahr = null;
    }
    if (!anfrage.gebaeudedaten.sanierungsdaten.leitungenHeizung) {
      anfrage.gebaeudedaten.sanierungsdaten.leitungenHeizungJahr = null;
    }
    if (!anfrage.gebaeudedaten.sanierungsdaten.rohreAusserhalb) {
      anfrage.gebaeudedaten.sanierungsdaten.rohreAusserhalbJahr = null;
    }
    if (!anfrage.gebaeudedaten.sanierungsdaten.rohreGebaeude) {
      anfrage.gebaeudedaten.sanierungsdaten.rohreGebaeudeJahr = null;
    }
    if (anfrage.gebaeudedaten.alleineigentuemer) {
      anfrage.gebaeudedaten.miteigentuemerTyp = "";
    }
    if (anfrage.gebaeudedaten.wandAnWand) {
      anfrage.gebaeudedaten.nachbargebaeudeImEigentum = false;
    }
    if (!anfrage.gebaeudedaten.eigenbauleistungen.erbracht) {
      anfrage.gebaeudedaten.eigenbauleistungen.betrag = 0;
      anfrage.gebaeudedaten.eigenbauleistungen.statik = false;
    }
  }

  function bereinigeFerienhaus(anfrage: Anfrage): void {
    bereinigeVericherungsBeginnFH(anfrage);
    bereinigeVorschaeden(
      anfrage.ferienhaus.vgv.tarifinformationen.vorschaedenAnzahl,
      anfrage.ferienhaus.vgv.vorschaeden,
    );
    bereinigeVorschaeden(
      anfrage.ferienhaus.vhv.tarifinformationen.vorschaedenAnzahl,
      anfrage.ferienhaus.vhv.vorschaeden,
    );
    bereinigeVorschaeden(
      anfrage.ferienhaus.hug.tarifinformationen.vorschaedenAnzahl,
      anfrage.ferienhaus.hug.vorschaeden,
    );
    bereinigeVorversicherung(anfrage.ferienhaus.vgv.vorversicherung, "vgv");
    bereinigeVorversicherung(anfrage.ferienhaus.vhv.vorversicherung, "vhv");
    bereinigeVorversicherung(anfrage.ferienhaus.hug.vorversicherung, "hug");
    bereinigeHochgeladeneDateien(anfrage);
  }

  function bereinigeVericherungsbeginn(anfrage: Anfrage): void {
    if (anfrage.tarifinformationen.versicherungsbeginn === "") {
      Logger.debug("anfrageBereinigung: Versicherungsbeginn war leer und wurde bereinigt.");
      if (pruefeNach12Uhr() || pruefe2902()) {
        anfrage.tarifinformationen.versicherungsbeginn = getIsoDateNowPlus(1);
      } else {
        anfrage.tarifinformationen.versicherungsbeginn = getIsoDateNow();
      }
    }
  }

  function bereinigeVericherungsBeginnFH(anfrage: Anfrage): void {
    if (anfrage.ferienhaus.vgv.tarifinformationen.versicherungsbeginn === "") {
      Logger.debug("anfrageBereinigung: Versicherungsbeginn VGV war leer und wurde bereinigt.");
      if (pruefeNach12Uhr() || pruefe2902()) {
        anfrage.ferienhaus.vgv.tarifinformationen.versicherungsbeginn = getIsoDateNowPlus(1);
      } else {
        anfrage.ferienhaus.vgv.tarifinformationen.versicherungsbeginn = getIsoDateNow();
      }
    }
    if (anfrage.ferienhaus.vhv.tarifinformationen.versicherungsbeginn === "") {
      Logger.debug("anfrageBereinigung: Versicherungsbeginn VHV war leer und wurde bereinigt.");
      if (pruefeNach12Uhr() || pruefe2902()) {
        anfrage.ferienhaus.vhv.tarifinformationen.versicherungsbeginn = getIsoDateNowPlus(1);
      } else {
        anfrage.ferienhaus.vhv.tarifinformationen.versicherungsbeginn = getIsoDateNow();
      }
    }
    if (anfrage.ferienhaus.hug.tarifinformationen.versicherungsbeginn === "") {
      Logger.debug("anfrageBereinigung: Versicherungsbeginn HUG war leer und wurde bereinigt.");
      if (pruefeNach12Uhr() || pruefe2902()) {
        anfrage.ferienhaus.hug.tarifinformationen.versicherungsbeginn = getIsoDateNowPlus(1);
      } else {
        anfrage.ferienhaus.hug.tarifinformationen.versicherungsbeginn = getIsoDateNow();
      }
    }
  }

  function emailValid(email: string) {
    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/.test(email);
  }

  function bereinigeHochgeladeneDateien(anfrage: Anfrage) {
    if (!enthaeltVGV(anfrage)) {
      anfrage.dateien = [];
    }
  }
}
